import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, Snackbar, Tooltip, useTheme } from '@mui/material';
import { Check, ContentCopy, FileDownload } from '@mui/icons-material';
import * as XLSX from 'xlsx';

/**
 * Tables inside Cal's answers, with "copy" and "export to Excel" on each one.
 *
 * Cal answers meter and mass-balance questions with markdown tables that people then want in a
 * spreadsheet — the fleet list in "what meters need attention?" is a work list, not just a reply.
 * Without this the only route is selecting the rendered table by hand, which loses the column
 * boundaries.
 *
 * The data is read back out of the rendered DOM table rather than from the markdown AST. The AST
 * route means walking children of children and reassembling cells from inline nodes (bold, code,
 * links all nest), and it has to be kept in step with however remark-gfm chooses to structure them.
 * The DOM already holds exactly what the user can see, which is also exactly what they expect to
 * paste, so one `querySelectorAll('tr')` is both simpler and more faithful.
 *
 * Copy writes TAB-separated text: that is what Excel, Numbers and Google Sheets all parse into
 * columns on paste. CSV would split on the commas inside Cal's own numbers ("1,996 days").
 */

const cellText = (el: Element) => (el.textContent || '').replace(/\s+/g, ' ').trim();

const readTable = (table: HTMLTableElement | null): string[][] => {
  if (!table) return [];
  return Array.from(table.querySelectorAll('tr'))
    .map((tr) => Array.from(tr.querySelectorAll('th,td')).map(cellText))
    .filter((r) => r.some((c) => c !== ''));
};

// Excel treats a leading =, +, - or @ as a formula. Cal emits "—" and "-" in cells constantly.
const deFormula = (v: string) => (/^[=+\-@]/.test(v) && !/^-?[\d.,]+$/.test(v) ? `'${v}` : v);

const CalTable: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const ref = useRef<HTMLTableElement>(null);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const copy = useCallback(async () => {
    const rows = readTable(ref.current);
    if (!rows.length) return;
    const tsv = rows.map((r) => r.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      // Clipboard API needs a secure context and permission; fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = tsv; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* nothing more to try */ }
      document.body.removeChild(ta);
    }
    setCopied(true); setToast(`${rows.length - 1} rows copied — paste straight into Excel`);
    setTimeout(() => setCopied(false), 1800);
  }, []);

  const toExcel = useCallback(() => {
    const rows = readTable(ref.current);
    if (!rows.length) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows.map((r) => r.map(deFormula)));
    ws['!cols'] = (rows[0] || []).map((_, i) =>
      ({ wch: Math.min(50, Math.max(10, ...rows.map((r) => (r[i] || '').length + 2))) }));
    XLSX.utils.book_append_sheet(wb, ws, 'Cal');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Cal-table_${stamp}.xlsx`);
    setToast('Exported to Excel');
  }, []);

  return (
    <Box sx={{ position: 'relative', '&:hover .cal-tbl-actions': { opacity: 1 } }}>
      <Box className="cal-tbl-actions" sx={{
        position: 'absolute', top: -6, right: 0, display: 'flex', gap: 0.25, zIndex: 2,
        opacity: 0.35, transition: 'opacity .15s',
        bgcolor: theme.palette.background.paper, borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}>
        <Tooltip title="Copy table">
          <IconButton size="small" onClick={copy} sx={{ p: 0.4 }}>
            {copied ? <Check sx={{ fontSize: 15, color: 'success.main' }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Export table to Excel">
          <IconButton size="small" onClick={toExcel} sx={{ p: 0.4 }}>
            <FileDownload sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <table ref={ref} style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2600} onClose={() => setToast(null)} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

/** Pass to <ReactMarkdown components={calMarkdownComponents}> wherever Cal's answers are rendered. */
export const calMarkdownComponents = {
  table: ({ children }: any) => <CalTable>{children}</CalTable>,
};

export default CalTable;
